/**
 * CheckoutSummary - 금액 요약 컴포넌트 (GROSS 역산 모델)
 *
 * netPrice: GROSS / 1.16 역산 순가격
 * tax: GROSS - NET 역산 세금
 * tipAmount: GROSS 기준 팁
 * pointsDiscount: 포인트 할인
 * total: GROSS + tip - discount (세금 별도 추가 없음)
 */
import { Loader2 } from 'lucide-react';

interface CheckoutSummaryProps {
  netPrice: number;
  tax: number;
  tipAmount: number;
  pointsDiscount: number;
  total: number;
  formatCurrency: (n: number) => string;
  estimatedPoints?: number;
  isEstimating?: boolean;
  /** #285: 적립 포인트 만료일수 (G4 툴팁용) */
  expiryDays?: number;
  labels: {
    netPrice: string;
    taxIVA: string;
    tip: string;
    pointsDiscount: string;
    total: string;
    estimatedPoints?: string;
    estimating?: string;
  };
}

export function CheckoutSummary({
  netPrice,
  tax,
  tipAmount,
  pointsDiscount,
  total,
  formatCurrency,
  estimatedPoints,
  isEstimating,
  expiryDays,
  labels,
}: CheckoutSummaryProps) {
  return (
    <div className="space-y-[10px]">
      <div className="flex justify-between text-[14px]">
        <span className="text-muted-foreground">{labels.netPrice}</span>
        <span className="font-medium">{formatCurrency(netPrice)}</span>
      </div>
      <div className="flex justify-between text-[14px]">
        <span className="text-muted-foreground">{labels.taxIVA}</span>
        <span className="font-medium">{formatCurrency(tax)}</span>
      </div>
      {tipAmount > 0 && (
        <div className="flex justify-between text-[14px]">
          <span className="text-muted-foreground">{labels.tip}</span>
          <span className="font-medium">{formatCurrency(tipAmount)}</span>
        </div>
      )}
      {pointsDiscount > 0 && (
        <div className="flex justify-between text-[14px]">
          <span className="text-muted-foreground">{labels.pointsDiscount}</span>
          <span className="font-medium text-green-600">-{formatCurrency(pointsDiscount)}</span>
        </div>
      )}
      <div className="h-[1px] bg-border" />
      <div className="flex justify-between">
        <span className="text-[16px] font-bold">{labels.total}</span>
        <span className="text-[18px] font-bold text-primary">{formatCurrency(total)}</span>
      </div>
      {(isEstimating || (estimatedPoints != null && estimatedPoints > 0)) && (
        <>
          <div className="h-[1px] border-t border-dashed border-border" />
          <div className="flex justify-between text-[14px]">
            <span
              className="text-muted-foreground"
              title={expiryDays ? `${expiryDays}일 후 소멸` : undefined}
            >
              {labels.estimatedPoints ?? '적립 예정 포인트'}
            </span>
            {isEstimating ? (
              <span className="flex items-center gap-[4px] text-muted-foreground">
                <Loader2 className="w-[14px] h-[14px] animate-spin" />
                {labels.estimating ?? '...'}
              </span>
            ) : (
              <span className="font-medium text-primary">+{estimatedPoints}P</span>
            )}
          </div>
        </>
      )}
    </div>
  );
}
